import { ENDPOINTS } from '../utils/endpoints';
import { getAPIwithToken, postAPIwithToken, postAPIwithoutToken } from '../utils/fetchApi';

// 1. Download Template API
export const downloadInventoryTemplateAPI = async (): Promise<void> => {
  const baseUrl: string = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const token: string | null = localStorage.getItem('gowardhan_erp_token');
  const endpoint = ENDPOINTS.INVENTORY_TEMPLATE.startsWith('/')
    ? ENDPOINTS.INVENTORY_TEMPLATE
    : `/${ENDPOINTS.INVENTORY_TEMPLATE}`;

  const response = await fetch(`${cleanBase}${endpoint}`, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    },
  });

  if (!response.ok) {
    let errorMsg = `Download failed with status ${response.status}`;
    try {
      const errJson = await response.json();
      if (errJson.message) errorMsg = errJson.message;
    } catch {
      // Ignore parse error
    }
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'stock_template.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// 2. Bulk Upload Inventory API
export interface UploadInventoryDetail {
  rowNumber: number;
  depoId: number;
  productId?: number;
  batchNumber?: string;
  quantity?: number;
  status: 'SUCCESS' | 'FAILED' | string;
  message?: string;
}

export interface UploadInventoryResponse {
  status: string;
  message: string;
  data?: {
    totalRows: number;
    successCount: number;
    failedCount: number;
    details?: UploadInventoryDetail[];
  };
}

export const uploadInventoryBulkAPI = async (file: File): Promise<UploadInventoryResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return await postAPIwithToken<UploadInventoryResponse>(ENDPOINTS.INVENTORY_UPLOAD, formData);
};

// 3. Live Depot Stock API
export interface DepoStockFiltersPayload {
  categoryId?: number | string;
  brandId?: number | string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface DepoStockItem {
  depoId: number;
  productId: number;
  totalQuantity: string | number;
  availableQuantity?: string | number;
  reservedQuantity?: string | number;
  product?: {
    id: number;
    name: string;
    displayName?: string;
    sapCode?: string;
    category?: {
      id: number;
      name: string;
    };
    brand?: {
      id: number;
      name: string;
    };
  };
}

export interface DepoStockResponse {
  status: string;
  data: {
    items: DepoStockItem[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const getDepoStockAPI = async (
  depoId: string | number,
  filters: DepoStockFiltersPayload = {}
): Promise<DepoStockResponse> => {
  const queryParams = new URLSearchParams();

  if (filters.categoryId !== undefined && filters.categoryId !== 'all') {
    queryParams.append('categoryId', String(filters.categoryId));
  }
  if (filters.brandId !== undefined && filters.brandId !== 'all') {
    queryParams.append('brandId', String(filters.brandId));
  }
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.page !== undefined) queryParams.append('page', String(filters.page));
  if (filters.limit !== undefined) queryParams.append('limit', String(filters.limit));

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${ENDPOINTS.DEPO_STOCK(depoId)}?${queryString}`
    : ENDPOINTS.DEPO_STOCK(depoId);

  return await getAPIwithToken<DepoStockResponse>(endpoint);
};

// 4. Query Stock Register API
export interface StockRegisterFiltersPayload {
  depoId?: number | string;
  productId?: number | string;
  batchNumber?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  search?: string;
  date?: string;
  city?: string;
  product?: string;
  depotId?: number | string; // Legacy field support
}

export type StockRegisterFilters = StockRegisterFiltersPayload;

export interface StockRegisterRecord {
  id: number | string;
  depoId: number;
  productId: number;
  batchNumber: string;
  quantity: string | number;
  mfgDate?: string;
  expiryDate?: string;
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
  depo?: {
    id: number;
    name: string;
    depoCode?: string;
  };
  product?: {
    id: number;
    name: string;
    sapCode?: string;
  };

  // Legacy frontend fields (optional mapping)
  skuCode?: string;
  productName?: string;
  category?: string;
  packSize?: string;
  unitType?: string;
  unitsPerCrate?: number;
  depotName?: string;
  openingStock?: number;
  inwardToday?: number;
  outwardToday?: number;
  damagedQty?: number;
  currentStock?: number;
  currentCrates?: number;
  safetyStockLevel?: number;
  mrp?: number;
  activeBatches?: number;
  earliestExpiry?: string;
  status?: string;
  lastUpdated?: string;
}

export interface StockRegisterResponse {
  status: string;
  data: {
    items: StockRegisterRecord[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const getStockRegisterAPI = async (
  filters: StockRegisterFiltersPayload = {}
): Promise<StockRegisterResponse> => {
  const queryParams = new URLSearchParams();

  const targetDepoId = filters.depoId ?? filters.depotId;
  if (targetDepoId !== undefined && targetDepoId !== 'all') {
    queryParams.append('depoId', String(targetDepoId));
  }
  if (filters.productId !== undefined && filters.productId !== 'all') {
    queryParams.append('productId', String(filters.productId));
  }
  if (filters.batchNumber) queryParams.append('batchNumber', filters.batchNumber);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);

  // Legacy/UI fallback fields
  if (filters.page !== undefined) queryParams.append('page', String(filters.page));
  if (filters.limit !== undefined) queryParams.append('limit', String(filters.limit));
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.date) queryParams.append('date', filters.date);
  if (filters.city && filters.city !== 'all') queryParams.append('city', filters.city);
  if (filters.product && filters.product !== 'all') queryParams.append('product', filters.product);

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${ENDPOINTS.INVENTORY_STOCK}?${queryString}`
    : ENDPOINTS.INVENTORY_STOCK;

  return await getAPIwithToken<StockRegisterResponse>(endpoint);
};

export const getInventoryStockRegister = getStockRegisterAPI;

// 5. Create Manual Stock Entry API (Single Entry Form)
export interface CreateStockEntryPayload {
  depoId: number;
  productId: number;
  batchNumber: string;
  quantity: number;
  mfgDate?: string;
  expiryDate?: string;
}

export interface CreateStockEntryResponse {
  status: string;
  message: string;
  data: {
    entry: StockRegisterRecord;
    inventory?: {
      id: number;
      depoId: number;
      productId: number;
      totalQuantity: string | number;
      reservedQuantity?: string | number;
      availableQuantity?: string | number;
      createdAt?: string;
      updatedAt?: string;
    };
  };
}

export const createManualStockEntryAPI = async (
  payload: CreateStockEntryPayload
): Promise<CreateStockEntryResponse> => {
  return await postAPIwithToken<CreateStockEntryResponse>(ENDPOINTS.INVENTORY_STOCK, payload);
};

// 5. Inward Daily Batch Stock (JSON Ingestion) API
export interface DepoInwardItem {
  productId?: number;
  productSapCode?: string;
  batchNumber: string;
  quantity: number;
  mfgDate?: string;
  expiryDate?: string;
  date?: string;
}

export interface DepoInwardPayload {
  syncReference: string;
  items: DepoInwardItem[];
}

export interface DepoInwardResponse {
  status: string;
  message: string;
  data?: {
    processed: number;
    errors?: any[];
  };
}

export const inwardDepoStockAPI = async (
  depoId: string | number,
  payload: DepoInwardPayload
): Promise<DepoInwardResponse> => {
  return await postAPIwithToken<DepoInwardResponse>(ENDPOINTS.DEPO_INWARD(depoId), payload);
};

// 6. Check Stock Availability API
export interface CheckAvailabilityItem {
  productId: number;
  quantity: number;
}

export interface CheckAvailabilityPayload {
  depoId: number;
  items: CheckAvailabilityItem[];
}

export interface InsufficientStockItem {
  productId: number;
  requested: number;
  available: number;
}

export interface CheckAvailabilityResponse {
  status: string;
  data: {
    isAvailable: boolean;
    insufficientItems: InsufficientStockItem[];
  };
}

export const checkStockAvailabilityAPI = async (
  payload: CheckAvailabilityPayload
): Promise<CheckAvailabilityResponse> => {
  return await postAPIwithoutToken<CheckAvailabilityResponse>(
    ENDPOINTS.CHECK_STOCK_AVAILABILITY,
    payload
  );
};
